import * as THREE from 'three';
import { Wall } from "./jsonParse";
import { CreateObject } from "./CreateObject";

export class PointInfo {
    constructor(public point: THREE.Vector3, public isExist: boolean, public isDelete: boolean = false) {
        ;
    }
}

export type WallType = {
    floor: number,
    info: WallToFitting[]
}
export type WallToFitting = {
    wallIndex: number,
    wallWidth: number,
    windowIndex: FittingType[],
    doorIndex: FittingType[],
    openingIndex: FittingType[]
}
export type FittingType = {
    index: number,
    center: THREE.Vector2,
    ratioFromStart: number,
    width: number,
    height: number,
    mountingHeight: number
}

export class WalltoFittingInfo {

    public info: WallType[];

    constructor(public pointList: PointInfo[][], public wall: Wall[][], public window: any, public door: any, public opening: any) {
        this.info = [];
    }

    /**
     * 壁と建具データの紐づけ
     *  
     * */
    public execute(): WallType[] {
        for (let i = 0; i < this.wall.length; i++) {
            const wallToFittingInfo: WallToFitting[] = [];
            for (let j = 0; j < this.wall[i].length; j++) {
                const wall = this.wall[i][j];
                const start = this.pointList[i][2 * j];
                const end = this.pointList[i][2 * j + 1];
                wallToFittingInfo.push({
                    wallIndex: j,
                    wallWidth: this.getWidth(wall),
                    windowIndex: this.createInfo(wall, this.window[i], start, end),
                    doorIndex: this.createInfo(wall, this.door[i], start, end),
                    openingIndex: this.createInfo(wall, this.opening[i], start, end)
                });
            }
            this.info.push({
                floor: i + 1,
                info: wallToFittingInfo
            });
        }
        return this.info;
    }

    /**
     * 建具のデータ構築
     * 
     * @param wall 壁データ
     * @param obj window, door, opening 建具類データ
     * @param start 始点 
     * @param end 終点 
     * */
    public createInfo(wall: Wall, obj: any, start: PointInfo, end: PointInfo): FittingType[] {
        const res: FittingType[] = [];
        const id = wall.identifier;
        for (let i = 0; i < obj.length; i++) {
            if(id == obj[i].parentIdentifier) {
                const fitInfo = {
                    index: i,
                    center: this.getCenter(obj[i]),
                    ratioFromStart: this.getRatio(this.getCenter(obj[i]), start, end),
                    width: this.getWidth(obj[i]),
                    height: this.getHeight(obj[i]),
                    mountingHeight: this.getMountingHeight(wall, obj[i])
                };
                res.push(fitInfo);
            }
        }
        return res;
    }

    /**
     * 建具の中点を取得
     * 
     * @param obj window, door, opening 建具類データ
     * */
    private getCenter(obj): THREE.Vector2 {
        const transform = obj.transform;
        const m = new THREE.Matrix4();
        m.elements = transform;
        const dimension = obj.dimensions;
        const width = dimension[0];
        // 建具からlineを作成する
        const lineObject = CreateObject.createWallLine(width, m, - dimension[1]/2);

        const euler = lineObject.rotation;
        // const points = lineObject.geometry.attributes.position.array;
        const points = (lineObject.geometry.attributes.position as THREE.BufferAttribute).array;
        const start = new THREE.Vector3(points[0], points[1], points[2]).applyEuler(euler);
        const end = new THREE.Vector3(points[3], points[4], points[5]).applyEuler(euler);
        const vec3 = end.clone().sub(start).divideScalar(2);
        const center = vec3.clone().add(start);

        return new THREE.Vector2(center.x, center.z);
    } 

    /**
     * 壁の始点からの割合を取得
     * 
     * @param fittingCenter 建具の中点
     * @param start 始点 
     * @param end 終点 
     * */
    private getRatio(fittingCenter: THREE.Vector2, start: PointInfo, end: PointInfo): number {
        const start_vec2 = new THREE.Vector2(start.point.x, start.point.z);
        const end_vec2 = new THREE.Vector2(end.point.x, end.point.z);
        const startToEnd = end_vec2.clone().sub(start_vec2);
        const startToFittingCenter = fittingCenter.clone().sub(start_vec2);

        return startToFittingCenter.clone().length() / startToEnd.clone().length();
    }

    /**
     * 幅
     * */
    private getWidth(obj): number {
        const dimension = obj.dimensions;
        return dimension[0];
    }

    /**
     * 建具高さ
     * */
    private getHeight(obj): number {
        const dimension = obj.dimensions;
        const height = dimension[1];
        return height;
    }

    /**
     * 壁の下端の算出
     * */
    private getUnderWall(wall: Wall): number {

        /** 壁の配置位置 */
        const wallHeight = wall.dimensions[1];
        const wallTransform = wall.transform;
        const m = new THREE.Matrix4();
        m.elements = wallTransform;
        const wallPosition = new THREE.Vector3();
        m.decompose(wallPosition, new THREE.Quaternion(), new THREE.Vector3());
        /** 壁 下基準 */
        const pos = this.positionFromMatrix(wall.transform);
        const base = pos.y - (wallHeight / 2);

        return base;
    }
    
    /**
     * 壁の下端の算出
     * */
    private positionFromMatrix(transform: number[]): THREE.Vector3 {
        const m = new THREE.Matrix4();
        m.elements = transform;
        const pos = new THREE.Vector3();
        m.decompose(pos, new THREE.Quaternion(), new THREE.Vector3());

        return pos;
    }

    /**
     * 建具取付高さの算出
     * */
    private getMountingHeight(wall: Wall, obj): number {

        /** 壁の下端の算出 */
        const base = this.getUnderWall(wall);

        /** 建具の配置位置 */
        const transform = obj.transform;
        const matrix = new THREE.Matrix4();
        matrix.elements = transform;        
        const position = new THREE.Vector3();
        matrix.decompose(position, new THREE.Quaternion(), new THREE.Vector3());
        /** 建具高さ */
        const height = obj.dimensions[1];

        /** 建具 上端高さ */
        const under = position.y + (height / 2);

        return Math.abs(under - base);
        
    }

    private find(id: string, obj: any): number[] {
        const res: number[] = [];
        for (let i = 0; i < obj.length; i++) {
            if(id == obj[i].parentIdentifier) res.push(i);
        }
        return res;
    }
}